import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FilterContext,
  ModalContext,
  PaginationContext,
  SearchContext,
  Button,
  Flex,
  useScreenResolution,
  ViewerContext,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";
import { DashFeaturesEnum } from "dash-common";
import { useGetBrokerPropertiesForListQuery } from "gql/generated.graphql";

import PropertyCard from "containers/properties/PropertyCard/PropertyCard";
import { StyledList } from "containers/properties/AllPropertiesListContainer/components/PropertiesContentView/components/PropertiesListView/PropertiesListView.styles";
import {
  PropertiesFilter,
  formatPropertiesFilter,
  formatPropertiesListLocationFilter,
} from "containers/properties/AllPropertiesListContainer/components/PropertiesContentView/components/PropertiesListView/PropertiesListView.helpers";
import { useAddPropertyToTrack } from "common/hooks/useAddPropertyToTrack/useAddPropertyToTrack";
import { GTMEventsEnum } from "common/google-tag-manager/constants";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import {
  buildAddress,
  buildBuiltRange,
  buildRangeString,
  isEmptyFilter,
} from "common/helpers";

const PropertiesListView = () => {
  const { account } = useContext(ViewerContext);
  const { t } = useTranslation(["common", "pages"]);
  const { setTotal, state, getRefetchProps, setPerPage } =
    useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const pagination = getRefetchProps(state);
  const searchText = searchSlug.length > 2 ? searchSlug : null;
  const { setPayload, toggleModal } = useContext(ModalContext);
  const { handleAddPropertyToTrack } = useAddPropertyToTrack();
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
  ]);
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();

  const {
    devices: { isTablet, isMobile },
  } = useScreenResolution();

  const { state: filterState, setFilterState } = useContext(FilterContext);

  const { data, loading } = useGetBrokerPropertiesForListQuery({
    fetchPolicy: "network-only",
    variables: {
      pagination: pagination,
      sort: filterState?.sort?.sortOptions,
      filter: {
        ...formatPropertiesFilter(filterState),
        ...formatPropertiesListLocationFilter(filterState),
        searchText,
        ...(!isSubscriptionActive && { isLimited: true }),
      },
    },
    onCompleted: (response) => {
      const total =
        response?.getBrokerProperties.totalCount ||
        data?.getBrokerProperties.totalCount ||
        0;
      setTotal(total);
    },
  });

  const { properties } = useMemo(
    () => ({
      properties: data?.getBrokerProperties.collection || [],
    }),
    [data]
  );

  const firstPropertyCenter = useMemo(() => {
    if (properties) {
      const coordinates = properties[0]?.location?.geoLocation?.coordinates;
      if (coordinates) {
        return {
          lng: coordinates[0],
          lat: coordinates[1],
        };
      }
    }
  }, [properties]);

  useEffect(() => {
    if (firstPropertyCenter) {
      setFilterState("center", firstPropertyCenter);
    }
  }, [firstPropertyCenter]);

  // FIXME: re-check next line
  useEffect(() => {
    setPerPage(12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (account) {
      const filters = {
        ...formatPropertiesFilter(filterState),
        ...formatPropertiesListLocationFilter(filterState),
        searchText,
      } as PropertiesFilter;
      if (!isEmptyFilter(filters) && !loading) {
        createGTMEvent({
          dataLayer: {
            event: GTMEventsEnum.PROPERTY_SEARCH,
            user_id: getGoogleTagManagerUserId(account.email),
            price_range: buildRangeString(filters.priceRange),
            property_type: filters.buildingTypes?.join(","),
            number_of_bedrooms: buildRangeString(filters.unitNumberOfBedrooms),
            number_of_bathrooms: buildRangeString(
              filters.unitNumberOfBathrooms
            ),
            appeared_date: filters.createdAt
              ? `From ${filters.createdAt.split("T")[0]}`
              : null,
            features_and_amenities: filters.amenities?.join(", "),
            completion_year: filters.completionDate,
            project_status: filters.projectStatuses?.join(", "),
            year_built: buildRangeString(filters.yearBuildRange),
            marketAreaIds: filters.marketAreaIds?.join(", "),
            search_term: filters.searchText,
          },
        });
      }
      if (properties.length) {
        properties.forEach((property) => {
          createGTMEvent({
            dataLayer: {
              event: GTMEventsEnum.PROPERTY_VIEWED,
              user_id: getGoogleTagManagerUserId(account.email),
              property_name: property.name,
              price_range: buildRangeString(property.priceRange),
              property_type: property.buildingTypes,
              number_of_bedrooms: buildRangeString(
                property.averageBedroomsNumberValue
              ),
              number_of_bathrooms: buildRangeString(
                property.averageBathroomsNumberValue
              ),
              features_and_amenities: property.amenityNames
                ?.map(({ name }) => name.trim())
                .join(", "),
              number_of_units: property.numberOfUnits,
              appeared_date: property.createdAt,
              completion_year: property.yearBuilt,
              project_status: property.projectStatus,
              year_built: buildBuiltRange({
                startDate: property.constructionStartDate,
                endDate: property.completionDate,
                yearOnly: true,
              }),
              scheduled_construction: buildBuiltRange({
                startDate: property.constructionStartDate,
                endDate: property.completionDate,
              }),
              marketAreaId: property?.marketArea?._id,
              address: buildAddress(property.location.address),
            },
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, account, filterState]);

  const labelToShow = !loading
    ? searchSlug
      ? t`common:placeholders.noSearchResults`
      : t`pages:propertyPage.noProperties`
    : "";

  return (
    <StyledList
      paginationProps={{
        nextButtonLabel: t("common:labels.next"),
        previousButtonLabel: t("common:labels.previous"),
      }}
      listProps={{
        w: "100%",
        loading,
        placeholderProps: {
          label: labelToShow,
        },
        loadingProps: { bgColor: "inherit" },
        grid: [343, 251, 356, 356],
        data: properties,
        gap: 16,
        mb: 2,
      }}
    >
      {(item) => (
        <PropertyCard
          property={item}
          renderOptions={() => (
            <Flex>
              <Button
                square={!isTablet && !isMobile}
                medium={isTablet || isMobile}
                mr={2}
                icon="folder"
                onClick={(e) => {
                  e.stopPropagation();
                  if (checkFeatureAccess()) {
                    setPayload({ propertyId: item._id });
                    toggleModal(true, "AddPropertyToPortfolio");
                  }
                }}
              >
                {(isTablet || isMobile) &&
                  t("pages:propertyPage.addToPortfolio")}
              </Button>

              <Button
                square
                medium={isTablet || isMobile}
                icon="bookmark"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddPropertyToTrack(item, account);
                }}
              />
            </Flex>
          )}
        />
      )}
    </StyledList>
  );
};

export default PropertiesListView;
